import { ArrowRight, CheckCircle2, ClipboardCheck } from "lucide-react";
import { navigate } from "../navigation";
import type { TeacherReviewPage } from "./review-list";
import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import "./analytics.css";

export function TeacherReviewPanel({ attemptId }: { attemptId: string }) {
  const [note, setNote] = useState("");
  const [feedback, setFeedback] = useState("");
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadedEndpoint, setLoadedEndpoint] = useState<string | null>(null);
  const [loadError, setLoadError] = useState("");
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [justSaved, setJustSaved] = useState(false);
  const [nextBusy, setNextBusy] = useState(false);
  const [queueMessage, setQueueMessage] = useState("");
  const pending = useRef(false);
  const endpoint = `/api/admin/attempts/${encodeURIComponent(attemptId)}/teacher-review`;
  const ready = loadedEndpoint === endpoint;

  useEffect(() => {
    const controller = new AbortController();
    void api<{ review: { note: string; feedback?: string; reviewedAt: number } | null }>(endpoint, { signal: controller.signal })
      .then(({ review }) => {
        if (controller.signal.aborted) return;
        setNote(review?.note ?? "");
        setFeedback(review?.feedback ?? "");
        setReviewed(Boolean(review));
        setLoadedEndpoint(endpoint);
        setLoadError("");
      })
      .catch((reason) => {
        if (!controller.signal.aborted) setLoadError(reason instanceof Error ? reason.message : "Could not load the review.");
      });
    return () => controller.abort();
  }, [endpoint, retry]);

  async function save() {
    if (!ready || pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      await api(endpoint, { method: "POST", body: JSON.stringify({ note, feedback }) });
      setReviewed(true); setJustSaved(true);
      window.dispatchEvent(new Event("sat:portal-invalidated"));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not save the review.");
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }

  async function openNext() {
    if (nextBusy) return;
    setNextBusy(true); setQueueMessage("");
    try {
      const page = await api<TeacherReviewPage>("/api/admin/reviews?status=unreviewed");
      const next = page.reviews.find((item) => item.attemptId !== attemptId);
      if (next) navigate(`#results=${encodeURIComponent(next.attemptId)}`);
      else setQueueMessage("Review queue is clear.");
    } catch (reason) { setQueueMessage(reason instanceof Error ? reason.message : "Could not load the review queue."); }
    finally { setNextBusy(false); }
  }

  return <aside className="teacher-review-panel">
    <div>
      <h2>{ready && reviewed ? <CheckCircle2 size={19} aria-hidden="true" /> : <ClipboardCheck size={19} aria-hidden="true" />}{ready && reviewed ? "Reviewed by your teaching team" : "Teacher review"}</h2>
      <p>Record your teaching notes and any feedback the student should see with this result.</p>
    </div>
    <div className="teacher-review-field"><label htmlFor="teacher-review-note">Private teacher note<textarea id="teacher-review-note" aria-describedby="teacher-note-visibility" value={ready ? note : ""} maxLength={1000} rows={3} disabled={!ready || busy} onChange={(event) => setNote(event.target.value)} placeholder="For example: revisit equivalent expressions in our next session." /></label><p id="teacher-note-visibility" className="teacher-review-hint">Only your teaching team can see this note.</p></div>
    <div className="teacher-review-field"><label htmlFor="teacher-review-feedback">Feedback for the student <span className="teacher-review-optional">Optional</span><textarea id="teacher-review-feedback" aria-describedby="teacher-feedback-visibility" value={ready ? feedback : ""} maxLength={2000} rows={4} disabled={!ready || busy} onChange={(event) => setFeedback(event.target.value)} placeholder="For example: review the slope questions, then try them again without a timer." /></label><p id="teacher-feedback-visibility" className="teacher-review-hint">Shown to the student on this result after you save. Clear this field and save to remove it.</p></div>
    {!ready && loadError && <div className="teacher-review-error"><p className="error" role="alert">Could not load the existing review. {loadError} Load it before making changes.</p><button type="button" onClick={() => { setLoadError(""); setRetry((value) => value + 1); }}>Retry loading review</button></div>}
    {error && <p className="error" role="alert">{error}</p>}
    <div className="teacher-review-actions">
      <button type="button" disabled={!ready || busy} onClick={() => void save()}>{busy ? "Saving…" : ready && reviewed ? "Save review" : "Save and mark as reviewed"}</button>
      <button type="button" className="teacher-review-next" disabled={nextBusy} onClick={() => void openNext()}>{nextBusy ? "Loading…" : justSaved ? "Next to review" : "Skip to next"} <ArrowRight size={14} aria-hidden="true" /></button>
    </div>
    <span role="status">{queueMessage || (!ready && !loadError ? "Loading review…" : justSaved ? "Saved. This result is out of the review queue." : ready && reviewed ? "This result is out of the review queue." : "")}</span>
  </aside>;
}
